<template>
  <v-dialog :value="value" max-width="500" @input="$emit('input', $event)">
    <div class="basic-info-popup">
      <strong class="basic-info-popup__title">임대계약신청</strong>

      <div class="basic-info-popup__field">
        <label>단지</label>
        <v-select
          v-model="form.village"
          :items="villageList"
          item-text="text"
          item-value="value"
          placeholder="단지를 선택하세요"
          solo
          flat
          dense
          hide-details
          class="basic-info-popup__select"
        ></v-select>
      </div>

      <div class="basic-info-popup__field">
        <label>이름</label>
        <input v-model.trim="form.name" type="text" maxlength="20" />
      </div>

      <div class="basic-info-popup__field">
        <label>전화번호</label>
        <input
          v-model="form.mobile"
          type="text"
          maxlength="13"
          @input="form.mobile = hyphen(form.mobile) || ''"
        />
      </div>

      <div class="basic-info-popup__field">
        <label>생년월일</label>
        <div class="basic-info-popup__birth-row">
          <v-select
            v-model="birth.year"
            :items="yearList"
            suffix="년"
            solo
            flat
            dense
            hide-details
            class="basic-info-popup__select"
          ></v-select>
          <v-select
            v-model="birth.month"
            :items="monthList"
            suffix="월"
            solo
            flat
            dense
            hide-details
            class="basic-info-popup__select"
          ></v-select>
          <v-select
            v-model="birth.day"
            :items="dayList"
            suffix="일"
            solo
            flat
            dense
            hide-details
            class="basic-info-popup__select"
          ></v-select>
        </div>
      </div>

      <div class="basic-info-popup__field">
        <label>성별</label>
        <v-radio-group row v-model="form.gender" class="pd0 mg0" hide-details>
          <v-radio value="M" label="남"></v-radio>
          <v-radio value="W" label="여"></v-radio>
        </v-radio-group>
      </div>

      <div class="basic-info-popup__field">
        <label>룸타입</label>
        <div class="basic-info-popup__room-types" v-if="roomTypeList.length">
          <v-radio-group
            v-model="form.type_room"
            row
            hide-details
            class="pd0 mg0"
          >
            <v-radio
              v-for="roomType in roomTypeList"
              :key="roomType.value"
              :value="roomType.value"
              :label="`${roomType.text}(${roomType.exclusive_area}m²)`"
              class="basic-info-popup__radio"
            ></v-radio>
          </v-radio-group>
        </div>
        <div class="basic-info-popup__room-types-empty" v-else>
          단지를 먼저 선택하세요
        </div>
      </div>

      <div class="basic-info-popup__footer">
        <button
          type="button"
          class="basic-info-popup__button is-close"
          @click="close"
        >
          취소
        </button>
        <button
          type="button"
          class="basic-info-popup__button is-submit"
          :disabled="!isFormReady || loading"
          @click="submit"
        >
          신청
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { hyphen } from "@/utils/filter.js";

export default {
  name: "PreContractRequestDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    villages: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        village: null,
        name: "",
        mobile: "",
        gender: "",
        type_room: "",
      },
      birth: {
        year: null,
        month: null,
        day: null,
      },
      currentYear: new Date().getFullYear(),
    };
  },
  computed: {
    villageList() {
      return this.villages.map((village) => ({
        text: village.village_name,
        value: village.id,
      }));
    },
    roomTypeList() {
      const village = this.villages.find(
        (village) => village.id === this.form.village
      );
      if (!village) return [];
      return village.roomTypes.map((roomType) => ({
        text: roomType.type_name,
        value: roomType.type_name,
        exclusive_area: roomType.exclusive_area ?? "0",
      }));
    },
    yearList() {
      const list = [];
      for (let year = this.currentYear; year >= this.currentYear - 100; year--) {
        list.push(year);
      }
      return list;
    },
    monthList() {
      const list = [];
      for (let month = 1; month <= 12; month++) {
        list.push(month);
      }
      return list;
    },
    dayList() {
      const lastDay = this.birth.year && this.birth.month
        ? new Date(this.birth.year, this.birth.month, 0).getDate()
        : 31;
      const list = [];
      for (let day = 1; day <= lastDay; day++) {
        list.push(day);
      }
      return list;
    },
    birthValue() {
      if (!this.birth.year || !this.birth.month || !this.birth.day) return "";
      const month = String(this.birth.month).padStart(2, "0");
      const day = String(this.birth.day).padStart(2, "0");
      return `${this.birth.year}-${month}-${day}`;
    },
    isFormReady() {
      return (
        this.form.village &&
        this.form.name &&
        this.form.mobile &&
        this.birthValue &&
        this.form.gender &&
        this.form.type_room
      );
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.init();
      }
    },
    "birth.year"() {
      this.clampDay();
    },
    "birth.month"() {
      this.clampDay();
    },
    "form.village"() {
      this.form.type_room = "";
    },
  },
  methods: {
    hyphen,
    init() {
      this.form = {
        village: null,
        name: "",
        mobile: "",
        gender: "",
        type_room: "",
      };
      this.birth = {
        year: null,
        month: null,
        day: null,
      };
    },
    clampDay() {
      if (this.birth.day && !this.dayList.includes(this.birth.day)) {
        this.birth.day = null;
      }
    },
    close() {
      this.$emit("input", false);
    },
    submit() {
      if (!this.isFormReady) {
        this.$store.commit("ERROR", "모든 항목을 입력해주세요.");
        return;
      }

      this.$emit("submit", {
        ...this.form,
        mobile: this.form.mobile.replace(/-/g, ""),
        birth: this.birthValue.replace(/-/g, ""),
        type_room: this.form.type_room,
      });
    },
  },
};
</script>

<style scoped>
.basic-info-popup {
  border-radius: 20px;
  background-color: #ffffff;
  padding: 26px 24px 22px;
  text-align: left;
}

.basic-info-popup__title {
  display: block;
  margin-bottom: 24px;
  text-align: center;
  color: #ef761d;
  font-size: 24px;
  font-weight: 700;
}

.basic-info-popup__field {
  margin-bottom: 16px;
  text-align: left;
}

.basic-info-popup__field label {
  display: block;
  margin-bottom: 8px;
  color: #b8b8b8;
  font-size: 14px;
  font-weight: 700;
}

.basic-info-popup__field input {
  width: 100%;
  height: 40px;
  border: 1px solid #252525;
  padding: 0 10px;
  font-size: 14px;
  color: #232323;
  text-align: left;
  box-sizing: border-box;
  border-radius: 6px;
}

.basic-info-popup__birth-row {
  display: flex;
  gap: 22px;
}

.basic-info-popup__birth-row .basic-info-popup__select {
  flex: 1 1 0;
}

.basic-info-popup__room-types {
  display: flex;
  align-items: center;
  min-height: 40px;
  border: 1px solid #252525;
  border-radius: 6px;
  padding: 6px 10px;
  box-sizing: border-box;
}

/* flex 자식이라 min-width:auto 로 인해 컨테이너보다 넓어지는 것을 방지 */
.basic-info-popup__room-types > .v-input {
  flex: 1 1 auto;
  min-width: 0;
}

/* 실제 줄바꿈은 v-radio-group 내부에서 일어나므로 행 간격도 여기에 준다 */
.basic-info-popup__room-types ::v-deep .v-input--radio-group__input {
  row-gap: 6px;
}

.basic-info-popup__radio {
  margin: 0 16px 0 0 !important;
  padding: 0 !important;
  flex: 0 1 auto;
  max-width: 100%;
  min-width: 0;
}

/* 라벨이 한 줄보다 길면 박스 안에서 줄바꿈 */
.basic-info-popup__radio ::v-deep .v-label {
  min-width: 0;
  white-space: normal;
  word-break: break-all;
}

.basic-info-popup__radio:last-of-type {
  margin-right: 0 !important;
}

.basic-info-popup__radio ::v-deep .v-input__slot {
  margin-bottom: 0;
}

.basic-info-popup__room-types-empty {
  height: 40px;
  display: flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 13px;
  color: #b8b8b8;
}

.basic-info-popup__select ::v-deep .v-input__slot {
  border: 1px solid #252525;
  border-radius: 6px;
  box-shadow: none !important;
}

.basic-info-popup__footer {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}

.basic-info-popup__button {
  height: 48px;
  border: 0;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
}

.basic-info-popup__button.is-close {
  flex: 0 0 110px;
  background-color: #d9d9d9;
  color: #232323;
}

.basic-info-popup__button.is-submit {
  flex: 1 1 auto;
  background-color: #ef761d;
  color: #ffffff;
}

.basic-info-popup__button.is-submit:disabled {
  opacity: 0.45;
}

@media (max-width: 359px) {
  .basic-info-popup {
    padding: 22px 18px 18px;
  }
}
</style>
